import { Dialog, DialogContent, DialogTitle, Tab, Tabs } from '@mui/material'
import type { APIAgentPrompt } from '@visorpro/client/dist/types/APIAgentPrompt'
import type { APIPrompt } from '@visorpro/client/dist/types/APIPrompt'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BreadCrumbs } from '../../components/breadcrumbs'
import { useModal } from '../../components/modal'
import { PromptForm } from '../../components/prompt-form/prompt-form'
import { PromptsTable } from '../../components/prompts-table/prompts-table'
import { AgentPromptsTable } from '../../components/agent-prompts-table/agent-prompts-table'
import { TextButton } from '../../components/text-button'
import { useAgentPrompts } from '../../stores/agent-prompts-store'
import { usePrompts } from '../../stores/prompts-store'
import type { Stores } from '../../util/store'
import {
    AgentPromptActionKind,
    AgentPromptActionsModal,
} from './agent-prompt-action-modal'
import { AssignOrganizationsModal } from './assign-organizations-modal'
import { PromptActionKind, PromptActionsModal } from './prompts-action-modal'

export interface PromptsListPageProps {
    stores: Stores
}

type TabKey = 'agent' | 'legacy'

export const PromptsListPage = observer(({ stores }: PromptsListPageProps) => {
    const navigate = useNavigate()
    const [tab, setTab] = useState<TabKey>('agent')
    const [now] = useState(() => DateTime.local())

    return (
        <div data-testid="prompts-page" style={{ minHeight: '100vh' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem 1rem 0',
                }}>
                <BreadCrumbs name="Prompts" path={[]} />

                {tab === 'agent' && (
                    <TextButton onClick={() => navigate('/prompts/new')}>
                        Create prompt
                    </TextButton>
                )}
            </div>

            <Tabs
                value={tab}
                onChange={(_, value: TabKey) => setTab(value)}
                sx={{ paddingLeft: '1rem', minHeight: 0 }}>
                <Tab
                    value="agent"
                    label="Vi 3.0"
                    data-testid="prompts-tab-agent"
                />
                <Tab
                    value="legacy"
                    label="Test runs (legacy)"
                    data-testid="prompts-tab-legacy"
                />
            </Tabs>

            {tab === 'agent' ? (
                <AgentPromptsTab stores={stores} now={now} />
            ) : (
                <LegacyPromptsTab stores={stores} now={now} />
            )}
        </div>
    )
})

const AgentPromptsTab = observer(
    ({ stores, now }: { stores: Stores; now: DateTime }) => {
        const navigate = useNavigate()
        const prompts = useAgentPrompts(stores)
        const menu = useModal<{ promptId: string }>()
        const [assigning, setAssigning] = useState<APIAgentPrompt | undefined>()
        const [showBaseline, setShowBaseline] = useState(false)

        const onShowMenu = useCallback(
            (e: React.MouseEvent, prompt: APIAgentPrompt) => {
                menu.open(e, { promptId: prompt.id })
            },
            [menu],
        )

        const onAction = useCallback(
            async (promptId: string, kind: AgentPromptActionKind) => {
                menu.close()
                const prompt = stores.agentPrompts.promptsById[promptId]

                switch (kind) {
                    case AgentPromptActionKind.Edit:
                        navigate(`/prompts/${promptId}`)
                        break
                    case AgentPromptActionKind.Assign:
                        setAssigning(prompt)
                        break
                    case AgentPromptActionKind.Promote:
                        await stores.agentPrompts.promote(promptId)
                        break
                    case AgentPromptActionKind.Delete:
                        await stores.agentPrompts.delete(promptId)
                        break
                }
            },
            [menu, navigate, stores],
        )

        const menuPrompt = menu.state?.data
            ? stores.agentPrompts.promptsById[menu.state.data.promptId]
            : undefined

        return (
            <div style={{ overflow: 'auto', padding: '0 1rem' }}>
                <ResolutionNote />

                <AgentPromptsTable
                    now={now}
                    prompts={prompts}
                    hasStoredDefault={
                        stores.agentPrompts.defaultPromptId !== undefined
                    }
                    onShowMenu={onShowMenu}
                    onAssign={setAssigning}
                    onViewBaseline={() => setShowBaseline(true)}
                    onDuplicateBaseline={() =>
                        navigate('/prompts/new?from=baseline')
                    }
                />

                {menu.state && menuPrompt && (
                    <AgentPromptActionsModal
                        anchor={menu.state.anchor}
                        promptId={menuPrompt.id}
                        isDefault={menuPrompt.is_default}
                        onClose={menu.close}
                        onAction={onAction}
                    />
                )}

                {assigning && (
                    <AssignOrganizationsModal
                        prompt={assigning}
                        stores={stores}
                        onClose={() => setAssigning(undefined)}
                        onSave={async organizationIds => {
                            const ok =
                                await stores.agentPrompts.setOrganizations(
                                    assigning.id,
                                    organizationIds,
                                )
                            if (ok) setAssigning(undefined)
                        }}
                    />
                )}

                {showBaseline && (
                    <Dialog
                        open
                        fullWidth
                        maxWidth="md"
                        onClose={() => setShowBaseline(false)}>
                        <DialogTitle>Built-in baseline</DialogTitle>
                        <DialogContent>
                            <pre
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '0.8rem',
                                }}>
                                {stores.agentPrompts.baselinePrompt}
                            </pre>
                        </DialogContent>
                    </Dialog>
                )}
            </div>
        )
    },
)

// Super admins otherwise have no way to know why an organization sees what it
// sees; the order is the one config.ts applies.
const ResolutionNote = () => (
    <p
        className="color-dimmed"
        style={{ fontSize: '0.8rem', padding: '0.75rem 0' }}>
        Chat picks a prompt in this order: the GreenLeaf hack room override, then
        the organization’s assigned prompt, then the promoted default, then the
        built-in baseline. Editing a prompt replaces the live text — there is no
        history and no rollback.
    </p>
)

const LegacyPromptsTab = observer(
    ({ stores, now }: { stores: Stores; now: DateTime }) => {
        const prompts = usePrompts(stores)
        const menu = useModal<{ promptId: string }>()
        const [form, setForm] = useState<{
            is_visible: boolean
            promptId?: string
        }>({ is_visible: false })

        const onShowMenu = useCallback(
            (e: React.MouseEvent, prompt: APIPrompt) => {
                menu.open(e, { promptId: prompt.id })
            },
            [menu],
        )

        return (
            <div style={{ display: 'flex' }}>
                <div
                    style={{
                        width: form.is_visible ? '50%' : '100%',
                        boxSizing: 'border-box',
                        overflow: 'auto',
                        paddingLeft: '1rem',
                    }}>
                    <div style={{ padding: '0.75rem 0' }}>
                        <TextButton
                            onClick={() => setForm({ is_visible: true })}>
                            Create prompt
                        </TextButton>
                    </div>

                    <PromptsTable
                        now={now}
                        onShowMenu={onShowMenu}
                        stores={stores}
                        prompts={prompts}
                    />
                </div>

                {form.is_visible && (
                    <PromptForm
                        onClose={() => setForm({ is_visible: false })}
                        promptId={form.promptId}
                        stores={stores}
                        style={{
                            width: '50%',
                            height: '100vh',
                            boxSizing: 'border-box',
                        }}
                    />
                )}

                {menu.state && (
                    <PromptActionsModal
                        anchor={menu.state.anchor}
                        promptId={menu.state.data!.promptId}
                        onClose={menu.close}
                        onAction={(promptId, kind) => {
                            if (kind === PromptActionKind.Delete) {
                                stores.prompts.delete(promptId)
                            } else {
                                setForm({ is_visible: true, promptId })
                            }
                            menu.close()
                        }}
                    />
                )}
            </div>
        )
    },
)
